import React, { useState } from 'react';
import { motion } from 'framer-motion';
import colors from '../styles/colors';

interface DescriptionProps {
  image: string;
  title: string;
  text: string;
  imageOnRight?: boolean; // If true, the image will appear on the right
  isFirst?: boolean; // Is this the first description?
  isLast?: boolean; // Is this the last description?
}

const Description: React.FC<DescriptionProps> = ({
  image,
  title,
  text,
  imageOnRight = true,
  isFirst = false,
  isLast = false,
}) => {
  const [isImageLoading, setIsImageLoading] = useState(true);

  // This function will be called when the image finishes loading
  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  return (
    <div
      className={`w-full p-8 flex flex-col lg:flex-row font-serif items-center overflow-hidden ${
        isFirst ? 'mt-24 mb-12' : '' // Add extra top margin for the first Description
      } ${isLast ? 'mb-24 mt-12' : ''}`} // Add extra bottom margin for the last Description
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* If image is on the left side */}
      {!imageOnRight && (
        <div
          className={`w-full lg:w-1/2 h-full flex justify-center items-center lg:mr-16 relative mb-8 lg:mb-0 order-1 lg:order-none`}
        >
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={image}
            loading='lazy'
            alt={title}
            className={`w-auto h-auto max-h-[90vh] object-cover rounded-lg shadow-lg ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={handleImageLoad}
          />
        </div>
      )}

      {/* Text section */}
      <div
        className={`w-full lg:w-1/3 p-6 lg:pl-12 lg:pr-12 ${imageOnRight ? 'lg:ml-12' : ''} text-center lg:text-left order-2 lg:order-none`}
        style={{ backgroundColor: colors.pastelCream }}
      >
        <motion.p
          className="lg:text-4xl text-2xl md:text-3xl mt-2 mb-8"
          style={{color: colors.darkBrown}}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{
            opacity: 1,
            y: 0,
            transition: { delay: 0.6, duration: 0.5 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {title}
        </motion.p>
        <motion.p
          className="lg:text-xl text-lg md:text-xl"
          style={{color: colors.darkBrown}}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{
            opacity: 1,
            y: 0,
            transition: { delay: 0.2, duration: 0.5 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {text}
        </motion.p>
      </div>

      {/* If image is on the right side */}
      {imageOnRight && (
        <div
          className="w-full lg:w-1/2 h-full flex justify-center items-center lg:ml-16 relative mb-8 lg:mb-0 order-1 lg:order-none"
        >
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={image}
            loading='lazy'
            alt={title}
            className={`w-full h-auto max-h-[90vh] object-cover rounded-lg shadow-lg ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={handleImageLoad}
          />
        </div>
      )}
    </div>
  );
};

export default Description;
